import { useMemo, useState } from 'react'
import { Button, GrantBarrierMark } from '../ui'
import { ApiError } from '../../api/client'
import { useCredentials, useCreateLocation, useDevices, useAccessGroups, useDoorCodes, useDoors, useGroups, useGroupsTree, useLocationTree, useMappingDecide, usePersons, useSchedules, useZones, SPLIT_LOCATION_TYPES } from '../../api/hooks'
import type { AccessSystem, LocationNode, MappingItem } from '../../api/types'
import { groupPaths } from '../../pages/Groups'

/** Which canonical entity a kind attaches/merges into — PROVIDER-specific,
 *  because the backend merge target is:
 *  - person → Person (all providers)
 *  - credential → Credential on techfass/axema (the vendor card merges onto
 *    an existing canonical credential, NOT onto its holder Person)
 *  - group → directory.Group (techfass)
 *  - location → directory.Group on techfass/axema (folders/org-groups adopt
 *    as Groups — Location(type='company') is gone), Location otherwise
 *  - zone/site → topology.Zone on axema (Vaka zones adopt as Zones),
 *    Location on acre (attach-only placement)
 *  - device → systems.Device on axema/came (the Device's GenericFK IS the
 *    binding), Location on acre (attach-only placement)
 *  - access_group → access.AccessGroup (techfass/axema)
 *  - schedule → access.Schedule (techfass/axema/came)
 *  - door → topology.Door (axema/techfass/came — the vendor door/reader binds
 *    onto an existing canonical door)
 *  - door_code → access.DoorCode (came — the grouped candidate's keypads
 *    bind onto an existing canonical code)
 *  Kinds not listed have no attach target today — the workbench hides the
 *  button for them. */
export type AttachTargetKind = 'location' | 'group' | 'person' | 'credential' | 'device' | 'access_group' | 'zone' | 'schedule' | 'door' | 'door_code'

/** Providers whose `location` kind adopts as a directory.Group (techfass
 *  folders, axema org groups). */
const groupLocationProviders: ReadonlySet<string> = new Set(['techfass', 'axema'])

export function attachTarget(provider: string, kind: string): AttachTargetKind | null {
  if (kind === 'person') return 'person'
  if (kind === 'credential' || kind === 'new_credential') return 'credential'
  if (kind === 'group') return 'group'
  if (kind === 'location') return groupLocationProviders.has(provider) ? 'group' : 'location'
  if (kind === 'zone') return provider === 'axema' ? 'zone' : 'location'
  if (kind === 'site') return 'location'
  if (kind === 'device') return provider === 'acre' ? 'location' : 'device'
  if (kind === 'access_group') return 'access_group'
  if (kind === 'schedule') return 'schedule'
  if (kind === 'door') return 'door'
  if (kind === 'door_code') return 'door_code'
  return null
}

/** Attach and merge land identically backend-side (both are AdoptDecision
 *  mode='merge'); the wording here is console-side only. */
export function isMergeTarget(targetKind: AttachTargetKind): boolean {
  return targetKind === 'person' || targetKind === 'credential'
}

interface Target {
  uid: string
  label: string
  sub?: string
}

/** One picker list row: a backend match hint (score + why) or a plain target. */
type PickerRow =
  | { hint: true; target: Target; score: number; why: string }
  | { hint: false; target: Target }

/** The picker's row order. Unfiltered: hints (backend score order) then the
 *  remaining targets. Filtered: a row whose NAME contains the query outranks
 *  a pure score hint — exact name matches lead, then the other name matches
 *  (hints first, score order), then sub-only matches; non-matching hints stay
 *  visible at the bottom. */
export function orderPickerRows(targets: Target[], item: MappingItem, query: string): PickerRow[] {
  const byUid = new Map(targets.map((t) => [t.uid, t]))
  const hints: PickerRow[] = []
  for (const s of item.suggestions) {
    const target = byUid.get(s.canonical_uid)
    if (target) hints.push({ hint: true, target, score: s.score, why: s.why })
  }
  const hintedUids = new Set(hints.map((h) => h.target.uid))
  const q = query.trim().toLowerCase()
  const plain: PickerRow[] = targets
    .filter((t) => !hintedUids.has(t.uid))
    .filter((t) => q === '' || t.label.toLowerCase().includes(q) || (t.sub ?? '').toLowerCase().includes(q))
    .map((target) => ({ hint: false, target }))
  if (q === '') return [...hints, ...plain]
  const nameHit = (r: PickerRow) => r.target.label.toLowerCase().includes(q)
  const exact = (r: PickerRow) => r.target.label.toLowerCase() === q
  const nameHits = [...hints, ...plain].filter(nameHit)
  const subOnly = plain.filter((r) => !nameHit(r))
  const misses = hints.filter((r) => !nameHit(r))
  return [...nameHits.filter(exact), ...nameHits.filter((r) => !exact(r)), ...subOnly, ...misses]
}

function flattenLocations(nodes: LocationNode[], depth = 0): Target[] {
  return nodes.flatMap((n) => [
    {
      uid: n.unique_id,
      label: `${'— '.repeat(depth)}${n.name}${n.inherits_grants === false ? ' 🔒' : ''}`,
      sub: n.type,
    },
    ...flattenLocations(n.children, depth + 1),
  ])
}

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** Spec §3 right rail: the compact canonical location tree, rendered beside
 *  the picker while attaching to a Location so the operator sees the spatial
 *  context. Clicking a node selects it as the target. */
function ContextTree({
  nodes,
  selected,
  onSelect,
  depth = 0,
}: {
  nodes: LocationNode[]
  selected: string | null
  onSelect: (uid: string) => void
  depth?: number
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {nodes.map((n) => (
        <li key={n.unique_id}>
          <button
            type="button"
            onClick={() => onSelect(n.unique_id)}
            title={n.inherits_grants === false ? "Requires direct assignment — grants above don't inherit in" : undefined}
            style={{ paddingLeft: depth * 12 }}
            className={`tnum flex w-full items-center gap-1 truncate rounded-sm px-1.5 py-0.5 text-left text-[11px] transition-colors ${
              n.unique_id === selected
                ? 'bg-ostrel-sand font-semibold text-ostrel-gold'
                : 'text-ostrel-ink-muted hover:bg-ostrel-sand/60 hover:text-ostrel-ink'
            }`}
          >
            <span className="truncate">{n.name}</span>
            {n.inherits_grants === false && <GrantBarrierMark />}
          </button>
          {n.children.length > 0 && (
            <ContextTree nodes={n.children} selected={selected} onSelect={onSelect} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  )
}

/** Inline attach/merge decision for one candidate row: a searchable list of
 *  canonical targets of the right kind (locations / groups / persons /
 *  credentials / devices — which one is provider-specific, see
 *  attachTarget), the backend's match hints ranked first with their `why`,
 *  and — for Location targets — the compact canonical tree beside the list.
 *  The backend treats attach and merge identically (both land as
 *  AdoptDecision mode='merge'); the action wording here is console-side
 *  only. On success the decide mutation invalidates the scope and the board
 *  refetches with the row bound. */
export function MappingAttachPicker({
  system,
  item,
  site,
  initialSelected,
  onDone,
}: {
  system: AccessSystem
  item: MappingItem
  /** The workbench's selected site — the decide mutation patches the
   *  site-scoped board cache. */
  site?: string
  /** Pre-selected target uid (a clicked suggestion) — the operator still
   *  confirms with the Attach/Merge button. */
  initialSelected?: string
  onDone: () => void
}) {
  const decide = useMappingDecide(system.unique_id, site)
  const createLocation = useCreateLocation()
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(initialSelected ?? null)
  const [newLocOpen, setNewLocOpen] = useState(false)
  const [newLocName, setNewLocName] = useState('')
  const [newLocType, setNewLocType] = useState<string>(SPLIT_LOCATION_TYPES[0])
  const [newLocParent, setNewLocParent] = useState('')

  const targetKind = attachTarget(system.provider, item.kind) as AttachTargetKind
  // New IDs attach (convert onto an existing credential) — 'merge' is the person/card wording.
  const action = item.kind === 'new_credential' ? 'attach' : isMergeTarget(targetKind) ? 'merge' : 'attach'

  const locationTree = useLocationTree()
  const groups = useGroups()
  const groupsTree = useGroupsTree()
  const persons = usePersons()
  const credentials = useCredentials()
  const devices = useDevices(system.unique_id)
  const accessGroups = useAccessGroups()
  const schedules = useSchedules()
  const zones = useZones()
  const doors = useDoors()
  const doorCodes = useDoorCodes()

  const targets: Target[] = useMemo(() => {
    if (targetKind === 'location') return flattenLocations(locationTree.data ?? [])
    if (targetKind === 'group') {
      // The right-side note is the group's PATH (parent chain) — the bare
      // kind ('organisation') told the operator nothing. Root groups (path
      // == name) show no note rather than duplicating the label.
      const paths = groupPaths(groupsTree.data ?? [])
      return (groups.data ?? []).map((g) => {
        const path = paths.get(g.unique_id)
        return { uid: g.unique_id, label: g.name, sub: path && path !== g.name ? path : undefined }
      })
    }
    if (targetKind === 'credential')
      return (credentials.data ?? []).map((c) => ({ uid: c.unique_id, label: c.code, sub: c.kind }))
    if (targetKind === 'device')
      // Already-bound devices are not attach targets — the backend 400s
      // already_adopted on them (bound is optional while the deploy rolls:
      // absent = keep showing).
      return (devices.data ?? [])
        .filter((d) => d.bound !== true)
        .map((d) => ({ uid: d.unique_id, label: d.name, sub: d.kind }))
    if (targetKind === 'access_group')
      return (accessGroups.data ?? []).map((g) => ({ uid: g.unique_id, label: g.name }))
    if (targetKind === 'schedule')
      return (schedules.data ?? []).map((s) => ({ uid: s.unique_id, label: s.name }))
    if (targetKind === 'zone') return (zones.data ?? []).map((z) => ({ uid: z.unique_id, label: z.name, sub: z.kind }))
    if (targetKind === 'door')
      return (doors.data ?? []).map((d) => ({
        uid: d.unique_id,
        label: d.name,
        sub: d.breadcrumb.join(' / ') || undefined,
      }))
    if (targetKind === 'door_code')
      return (doorCodes.data ?? []).map((c) => ({
        uid: c.unique_id,
        label: c.code,
        sub: c.label || undefined,
      }))
    return (persons.data ?? []).map((p) => ({
      uid: p.unique_id,
      label: p.display_name,
      sub: p.emails[0] ?? undefined,
    }))
  }, [targetKind, locationTree.data, groups.data, groupsTree.data, persons.data, credentials.data, devices.data, accessGroups.data, schedules.data, zones.data, doors.data, doorCodes.data])

  /** Row order: unfiltered, hints (backend score order) then the remaining
   *  targets; filtered, name-substring matches outrank pure score hints —
   *  see orderPickerRows. Hints always stay visible. */
  const rows = useMemo(
    () => orderPickerRows(targets, item, query),
    [targets, item, query],
  )

  const loading =
    (targetKind === 'location' && (locationTree.isPending || (locationTree.isFetching && rows.length === 0))) ||
    (targetKind === 'group' && (groups.isPending || (groups.isFetching && rows.length === 0))) ||
    (targetKind === 'person' && (persons.isPending || (persons.isFetching && rows.length === 0))) ||
    (targetKind === 'credential' && (credentials.isPending || (credentials.isFetching && rows.length === 0))) ||
    (targetKind === 'device' && (devices.isPending || (devices.isFetching && rows.length === 0))) ||
    (targetKind === 'access_group' && (accessGroups.isPending || (accessGroups.isFetching && rows.length === 0))) ||
    (targetKind === 'schedule' && (schedules.isPending || (schedules.isFetching && rows.length === 0))) ||
    (targetKind === 'zone' && (zones.isPending || (zones.isFetching && rows.length === 0))) ||
    (targetKind === 'door' && (doors.isPending || (doors.isFetching && rows.length === 0))) ||
    (targetKind === 'door_code' && (doorCodes.isPending || (doorCodes.isFetching && rows.length === 0)))

  const submit = () => {
    if (decide.isPending || !selected) return
    decide.mutate(
      { kind: item.kind, vendor_uid: item.vendor_uid, action, target: selected },
      { onSuccess: onDone },
    )
  }

  /** Inline location creation (acre zone/device names are often useless, so
   *  the operator picks the structure manually). On success the new node
   *  becomes the selected attach target and the form collapses — the tree
   *  refetch comes from the mutation's scope invalidation. */
  const submitNewLocation = () => {
    const name = newLocName.trim()
    if (createLocation.isPending || name === '') return
    createLocation.mutate(
      { name, type: newLocType, parent: newLocParent === '' ? null : newLocParent },
      {
        onSuccess: (loc) => {
          setSelected(loc.unique_id)
          setNewLocOpen(false)
          setNewLocName('')
          setNewLocParent('')
        },
      },
    )
  }

  const rowClass = (uid: string) =>
    `flex w-full flex-col gap-0.5 rounded-sm border px-3 py-2 text-left transition-colors ${
      uid === selected
        ? 'border-ostrel-gold bg-ostrel-sand'
        : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
    }`

  return (
    <form
      className="flex flex-col gap-3 rounded-sm border border-ostrel-line bg-ostrel-sand/40 px-3 py-2.5"
      onSubmit={(e) => {
        e.preventDefault()
        submit()
      }}
    >
      <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        {action === 'merge'
          ? `Merge into existing ${targetKind.replace(/_/g, ' ')}`
          : `Attach to existing ${targetKind.replace(/_/g, ' ')}`}
      </p>
      <div className={`grid gap-3 ${targetKind === 'location' ? 'md:grid-cols-[1fr_180px]' : ''}`}>
        <div className="flex min-w-0 flex-col gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter targets…"
            className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
          />
          {targetKind === 'location' &&
            (newLocOpen ? (
              <div className="flex flex-col gap-2 rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2">
                <input
                  type="text"
                  value={newLocName}
                  onChange={(e) => setNewLocName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      submitNewLocation()
                    }
                  }}
                  placeholder="Location name"
                  aria-label="New location name"
                  className="rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-[11px] text-ostrel-ink-muted">
                    Type
                    <select
                      aria-label="New location type"
                      value={newLocType}
                      onChange={(e) => setNewLocType(e.target.value)}
                      className="rounded-sm border border-ostrel-line bg-ostrel-card px-1.5 py-0.5 text-[11px] text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                    >
                      {SPLIT_LOCATION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-ostrel-ink-muted">
                    Parent
                    <select
                      aria-label="New location parent"
                      value={newLocParent}
                      onChange={(e) => setNewLocParent(e.target.value)}
                      className="min-w-0 flex-1 rounded-sm border border-ostrel-line bg-ostrel-card px-1.5 py-0.5 text-[11px] text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
                    >
                      <option value="">— root —</option>
                      {targets.map((t) => (
                        <option key={t.uid} value={t.uid}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {createLocation.error && (
                  <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
                    {errDetail(createLocation.error)}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setNewLocOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="navy"
                    size="sm"
                    disabled={createLocation.isPending || newLocName.trim() === ''}
                    onClick={submitNewLocation}
                  >
                    {createLocation.isPending ? 'Creating…' : 'Create location'}
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setNewLocOpen(true)}
                className="self-start rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-1 text-[11px] font-medium text-ostrel-ink-muted transition-colors hover:bg-ostrel-sand hover:text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
              >
                New location
              </button>
            ))}
          {loading ? (
            <p className="tnum px-1 text-[11px] text-ostrel-ink-muted">Loading targets…</p>
          ) : (
            <ul className="flex max-h-64 flex-col gap-1.5 overflow-y-auto">
              {rows.map((r) => (
                <li key={r.target.uid}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.target.uid)}
                    className={rowClass(r.target.uid)}
                  >
                    <span className="flex items-center gap-2">
                      {r.hint && (
                        <span className="tnum text-[11px] text-ostrel-gold">
                          {Math.round(r.score * 100)}%
                        </span>
                      )}
                      <span
                        className={`min-w-0 truncate text-sm text-ostrel-ink ${r.hint ? 'font-medium' : ''}`}
                      >
                        {r.target.label}
                      </span>
                      {r.target.sub && (
                        <span className="tnum ml-auto truncate text-[11px] text-ostrel-ink-muted">
                          {r.target.sub}
                        </span>
                      )}
                    </span>
                    {r.hint && (
                      <span className="truncate text-[11px] text-ostrel-ink-muted">{r.why}</span>
                    )}
                  </button>
                </li>
              ))}
              {rows.length === 0 && (
                <li className="tnum px-1 py-2 text-[11px] text-ostrel-ink-muted">
                  No matching targets.
                </li>
              )}
            </ul>
          )}
        </div>
        {targetKind === 'location' && (
          <div className="rounded-sm border border-ostrel-line bg-ostrel-card px-2 py-2">
            <p className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Locations
            </p>
            <div className="max-h-64 overflow-y-auto">
              <ContextTree nodes={locationTree.data ?? []} selected={selected} onSelect={setSelected} />
            </div>
          </div>
        )}
      </div>
      {decide.error && (
        <p className="rounded-sm border border-ostrel-danger/40 bg-ostrel-danger/10 px-3 py-2 text-xs text-ostrel-danger">
          {errDetail(decide.error)}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" variant="navy" size="sm" disabled={decide.isPending || !selected}>
          {decide.isPending
            ? action === 'merge'
              ? 'Merging…'
              : 'Attaching…'
            : action === 'merge'
              ? 'Merge'
              : 'Attach'}
        </Button>
      </div>
    </form>
  )
}
